import React, { useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { updateInfiniteProfile } from '~/services/LoyaltyService';

import type { UpdateInfiniteProfileRequest } from '~/types/loyaltyContract';

import {
  type BookingAuthorityFormState,
  type InfiniteProfileFormState,
  TITLE_OPTIONS,
  emptyBookingAuthority,
  getInitialBookingAuthorities,
  getInitialProfileForm,
} from './LuxLoyaltyInfiniteProfileDetails.utils';

export default function InfiniteProfileEditDialog({
  profile,
  open,
  onClose,
  onSaved,
}: {
  profile: App.LuxLoyaltyInfiniteProfile;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const isPrimaryProfile = profile.role === 'PRIMARY';
  const [profileForm, setProfileForm] = useState<InfiniteProfileFormState>(() => getInitialProfileForm(profile));
  const [bookingAuthorities, setBookingAuthorities] = useState<Array<BookingAuthorityFormState>>(() =>
    getInitialBookingAuthorities(profile),
  );
  const [hasUpdatedBookingAuthorities, setHasUpdatedBookingAuthorities] = useState(false);

  useEffect(() => {
    if (open) {
      setProfileForm(getInitialProfileForm(profile));
      setBookingAuthorities(getInitialBookingAuthorities(profile));
      setHasUpdatedBookingAuthorities(false);
    }
  }, [open, profile]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload: UpdateInfiniteProfileRequest = {
        profile: {
          title: profileForm.title || null,
          firstName: profileForm.firstName,
          lastName: profileForm.lastName,
          email: profileForm.email,
          phonePrefix: profileForm.phonePrefix,
          phone: profileForm.phone,
          ...(isPrimaryProfile
            ? {
                address: profileForm.address,
                address2: profileForm.address2?.trim() || null,
                city: profileForm.city,
                postcode: profileForm.postcode,
                state: profileForm.state,
                country: profileForm.country,
                welcomePackNotes: profileForm.welcomePackNotes?.trim() || null,
              }
            : {}),
        },
      };

      if (hasUpdatedBookingAuthorities) {
        payload.bookingAuthorities = bookingAuthorities
          .filter((authority) => authority.id || !authority.isDeactivated)
          .map((authority) => ({
            ...authority,
            title: authority.title || null,
            isDeactivated: !!authority.isDeactivated,
          }));
      }

      return updateInfiniteProfile(profile.customerId, payload);
    },
    onSuccess: async () => {
      enqueueSnackbar('Infinite profile updated', { variant: 'success' });
      await onSaved();
      onClose();
    },
    onError: (error) => {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to update Infinite profile', {
        variant: 'error',
      });
    },
  });

  const isSaving = mutation.isPending;
  const hasInvalidBookingAuthority = bookingAuthorities.some(
    (authority) =>
      !authority.isDeactivated &&
      (!authority.firstName.trim() ||
        !authority.lastName.trim() ||
        !authority.email.trim() ||
        !authority.phonePrefix.trim() ||
        !authority.phone.trim()),
  );
  const canSave =
    !!profileForm.firstName?.trim() &&
    !!profileForm.lastName?.trim() &&
    !!profileForm.email?.trim() &&
    !!profileForm.phonePrefix?.trim() &&
    !!profileForm.phone?.trim() &&
    (!isPrimaryProfile ||
      (!!profileForm.address?.trim() &&
        !!profileForm.city?.trim() &&
        !!profileForm.postcode?.trim() &&
        !!profileForm.state?.trim() &&
        !!profileForm.country?.trim())) &&
    !hasInvalidBookingAuthority;

  const updateAuthority = (index: number, patch: Partial<BookingAuthorityFormState>) => {
    setHasUpdatedBookingAuthorities(true);
    setBookingAuthorities((current) =>
      current.map((authority, authorityIndex) => (authorityIndex === index ? { ...authority, ...patch } : authority)),
    );
  };

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Infinite profile</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            <TextField
              select
              label="Title"
              value={profileForm.title ?? ''}
              onChange={(event) =>
                setProfileForm((current) => ({
                  ...current,
                  title: (event.target.value || null) as InfiniteProfileFormState['title'],
                }))
              }
            >
              {TITLE_OPTIONS.map((option) => (
                <MenuItem key={option.value || 'none'} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Box />
            <TextField
              label="First name"
              value={profileForm.firstName ?? ''}
              onChange={(event) => setProfileForm((current) => ({ ...current, firstName: event.target.value }))}
              required
            />
            <TextField
              label="Last name"
              value={profileForm.lastName ?? ''}
              onChange={(event) => setProfileForm((current) => ({ ...current, lastName: event.target.value }))}
              required
            />
            <TextField
              label="Email"
              value={profileForm.email ?? ''}
              onChange={(event) => setProfileForm((current) => ({ ...current, email: event.target.value }))}
              required
            />
            <Stack direction="row" spacing={1}>
              <TextField
                label="Phone prefix"
                value={profileForm.phonePrefix ?? ''}
                onChange={(event) => setProfileForm((current) => ({ ...current, phonePrefix: event.target.value }))}
                required
                sx={{ width: 140 }}
              />
              <TextField
                label="Phone"
                value={profileForm.phone ?? ''}
                onChange={(event) => setProfileForm((current) => ({ ...current, phone: event.target.value }))}
                required
                fullWidth
              />
            </Stack>
            {isPrimaryProfile && (
              <>
                <TextField
                  label="Address"
                  value={profileForm.address ?? ''}
                  onChange={(event) => setProfileForm((current) => ({ ...current, address: event.target.value }))}
                  required
                />
                <TextField
                  label="Address 2"
                  value={profileForm.address2 ?? ''}
                  onChange={(event) => setProfileForm((current) => ({ ...current, address2: event.target.value }))}
                />
                <TextField
                  label="City"
                  value={profileForm.city ?? ''}
                  onChange={(event) => setProfileForm((current) => ({ ...current, city: event.target.value }))}
                  required
                />
                <TextField
                  label="State"
                  value={profileForm.state ?? ''}
                  onChange={(event) => setProfileForm((current) => ({ ...current, state: event.target.value }))}
                  required
                />
                <TextField
                  label="Postcode"
                  value={profileForm.postcode ?? ''}
                  onChange={(event) => setProfileForm((current) => ({ ...current, postcode: event.target.value }))}
                  required
                />
                <TextField
                  label="Country"
                  value={profileForm.country ?? ''}
                  onChange={(event) => setProfileForm((current) => ({ ...current, country: event.target.value }))}
                  required
                />
                <TextField
                  label="Welcome pack notes"
                  value={profileForm.welcomePackNotes ?? ''}
                  onChange={(event) =>
                    setProfileForm((current) => ({ ...current, welcomePackNotes: event.target.value }))
                  }
                  multiline
                  minRows={3}
                  sx={{ gridColumn: { md: '1 / -1' } }}
                />
              </>
            )}
          </Box>

          <Divider />

          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Booking authorities ({bookingAuthorities.length})
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setHasUpdatedBookingAuthorities(true);
                  setBookingAuthorities((current) => [...current, emptyBookingAuthority()]);
                }}
              >
                Add booking authority
              </Button>
            </Stack>

            {bookingAuthorities.map((authority, index) => (
              <Paper key={authority.id ?? `new-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Booking authority {index + 1}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!authority.isDeactivated}
                          onChange={(event) => updateAuthority(index, { isDeactivated: event.target.checked })}
                        />
                      }
                      label="Deactivated"
                    />
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                      select
                      label="Title"
                      value={authority.title ?? ''}
                      onChange={(event) =>
                        updateAuthority(index, {
                          title: (event.target.value || null) as BookingAuthorityFormState['title'],
                        })
                      }
                      sx={{ minWidth: 140 }}
                    >
                      {TITLE_OPTIONS.map((option) => (
                        <MenuItem key={option.value || 'none'} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                      gap: 2,
                    }}
                  >
                    <TextField
                      label="First name"
                      value={authority.firstName}
                      onChange={(event) => updateAuthority(index, { firstName: event.target.value })}
                      required={!authority.isDeactivated}
                    />
                    <TextField
                      label="Last name"
                      value={authority.lastName}
                      onChange={(event) => updateAuthority(index, { lastName: event.target.value })}
                      required={!authority.isDeactivated}
                    />
                    <TextField
                      label="Email"
                      value={authority.email}
                      onChange={(event) => updateAuthority(index, { email: event.target.value })}
                      required={!authority.isDeactivated}
                    />
                    <Stack direction="row" spacing={1}>
                      <TextField
                        label="Phone prefix"
                        value={authority.phonePrefix}
                        onChange={(event) => updateAuthority(index, { phonePrefix: event.target.value })}
                        required={!authority.isDeactivated}
                        sx={{ width: 140 }}
                      />
                      <TextField
                        label="Phone"
                        value={authority.phone}
                        onChange={(event) => updateAuthority(index, { phone: event.target.value })}
                        required={!authority.isDeactivated}
                        fullWidth
                      />
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => mutation.mutate()} disabled={!canSave || isSaving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
